import { IconButton } from '@greystar/designsystem';
import {
  FeeActions,
  getOneTimePetFees,
  isFeePerApplicant,
  getDictionaryEntry,
} from '@greystar/lib';
import { useEffect, useState } from 'react';
import FeeLine from '../../FeeLine/FeeLine';
import styles from './counter.module.scss';
import CounterProps from './Counter.props';
import { useI18n } from 'next-localization';

export default function Counter(props: CounterProps) {
  const { t } = useI18n();
  const [count, setCount] = useState(props.defaultValue);
  const isFeeCounter = !props.applicantCounter;
  const isOptionalPerApplicantFee = isFeeCounter && props.fee && isFeePerApplicant(props.fee);

  // For an optional fee, changeAmount is the number of applicants.
  const changeAmount = isOptionalPerApplicantFee ? props.feeState.numberOfApplicants : 1;

  const maxValue =
    isFeeCounter && isOptionalPerApplicantFee
      ? props.feeState.numberOfApplicants
      : props.maxThreshold;

  let minValue: number;
  if (isFeeCounter) {
    if (isOptionalPerApplicantFee) {
      minValue = 0;
    } else {
      minValue = props.minThreshold ?? 0;
    }
  } else {
    minValue = props.minThreshold ?? 1;
  }

  // useEffect to update the fee counter when props.numberOfApplicants changes.
  // For optional per applicant fees, we only update if the fee has been enabled (i.e. count > 0).
  useEffect(() => {
    if (isFeeCounter && isOptionalPerApplicantFee && count > 0 && props.fee) {
      if (count !== props.feeState.numberOfApplicants) {
        if (props.feeState.numberOfApplicants > count) {
          props.handlePlus({
            selectedFee: props.fee,
            action: FeeActions.INCREMENT,
            changeAmount: props.feeState.numberOfApplicants - count,
          });
        } else {
          props.handleMinus({
            selectedFee: props.fee,
            action: FeeActions.DECREMENT,
            changeAmount: props.feeState.numberOfApplicants,
          });
        }
        setCount(props.feeState.numberOfApplicants);
      }
    }
  }, [
    props.feeState.numberOfApplicants,
    count,
    isFeeCounter,
    isOptionalPerApplicantFee,
    props.fee,
    props.handlePlus,
    props.handleMinus,
    props,
  ]);

  const handleIncrement = () => {
    if (isFeeCounter) {
      if (isOptionalPerApplicantFee) {
        // For optional per applicant fees, if disabled (count === 0), enable it by setting to numberOfApplicants.
        if (count === 0) {
          props.handlePlus({
            selectedFee: props.fee,
            action: FeeActions.INCREMENT,
            changeAmount: props.feeState.numberOfApplicants,
          });
          setCount(props.feeState.numberOfApplicants);
        }
      } else if (count < maxValue) {
        const newCount = Math.min(count + changeAmount, maxValue);
        props.handlePlus({
          selectedFee: props.fee,
          action: FeeActions.INCREMENT,
          changeAmount: newCount - count,
        });
        setCount(newCount);
      }
    } else if (count < maxValue) {
      // APPLICANT LOGIC
      const newCount = Math.min(count + 1, maxValue);
      props.handlePlus({
        selectedFee: props.fee,
        action: FeeActions.INCREMENT,
        changeAmount: newCount,
      });
      setCount(newCount);
    }
  };

  const handleDecrement = () => {
    if (isFeeCounter) {
      if (isOptionalPerApplicantFee) {
        // For optional per applicant fees, if enabled, disable it by setting count to 0.
        if (count > 0) {
          props.handleMinus({
            selectedFee: props.fee,
            action: FeeActions.RESET,
            changeAmount: 0,
          });
          setCount(0);
        }
      } else if (count > minValue) {
        const newCount = Math.max(count - changeAmount, minValue);
        props.handleMinus({
          selectedFee: props.fee,
          action: FeeActions.DECREMENT,
          changeAmount: count - newCount,
        });
        setCount(newCount);
      }
    } else if (count > minValue) {
      // APPLICANT LOGIC
      const newCount = Math.max(count - 1, minValue);
      props.handleMinus({
        action: FeeActions.DECREMENT,
        changeAmount: newCount,
      });
      setCount(newCount);
    }
  };

  const selectedOneTimePetFees = getOneTimePetFees(props.feeState.allFees, props.fee, count);

  const label = props.label ?? props.fee?.label ?? '';

  const showLabel = () => {
    if (props.applicantCounter) {
      return <p className={styles['fee-counter-counter-label']}>{label}</p>;
    } else if (props.fee) {
      return (
        <FeeLine
          {...props}
          fee={props.fee}
          description={label}
          className={styles['fee-counter-fee-line']}
        />
      );
    } else return <></>;
  };

  return (
    <div className={`${props.className ?? ''} ${styles['fee-counter']}`}>
      <div className={styles['fee-counter-counter']}>
        {showLabel()}
        <div className={styles['fee-counter-counter-controls']}>
          <IconButton
            onClick={handleDecrement}
            iconName={'Minus'}
            isDisabled={count === props.minThreshold}
            variant={'floatingPrimary'}
            aria-label={getDictionaryEntry(t('counter-decrement'), 'Counter Decrement')}
          />
          <span>{count}</span>
          <IconButton
            onClick={handleIncrement}
            iconName={'Plus'}
            isDisabled={count === props.maxThreshold}
            variant={'floatingPrimary'}
            aria-label={getDictionaryEntry(t('counter-increment'), 'Counter Increment')}
          />
        </div>
      </div>
      <div>
        {selectedOneTimePetFees?.map((fee) => (
          <FeeLine
            {...props}
            key={fee.id}
            fee={fee}
            description={fee.label}
            className={styles['fee-counter-subline']}
          />
        ))}
      </div>
    </div>
  );
}
